import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';
import { useOperationCatalog } from '@/api/useOperationCatalog';
import type { OperationTypeDefinition, Duration } from '@/graph/domain/types';
import type { AddStepFormState, AddStepModalProps } from './types';
import { INITIAL_FORM_STATE } from './types';
import { formToOperationSpec } from './formToSpec';
import { buildOperationTemplate } from './buildOperationTemplate';
import { OperationsToolbox } from './OperationsToolbox';
import { StepDetailsPanel } from './StepDetailsPanel';
import type { StepValidationFocusTarget } from './StepDetailsPanel';
import { StepSidePanel } from './StepSidePanel';
import { TransferDetailsPanel } from './TransferDetailsPanel';
import type { TransferFormState } from '@/graph-store/actions/transferHelpers';
import { ComplexDetailsPanel } from './ComplexDetailsPanel';
import type { ComplexFormState } from './ComplexDetailsPanel';
import type { AddComplexStepInput } from '@/graph-store/actions/complexActions';
import { validateUniqueField } from '@/api/uniqueFieldClient';

const MAX_RECOMMENDED_INSTRUMENTS = 5;

function sameStringArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Extract a single numeric value from raw data for range validation
 */
function extractRangeScalarValue(raw: unknown): number | string {
  if (raw == null) return '';
  if (typeof raw === 'string' || typeof raw === 'number') {
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : '';
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    if (obj.value != null) return extractRangeScalarValue(obj.value);
    if (obj.amount != null) return extractRangeScalarValue(obj.amount);
    if (obj.label != null) return extractRangeScalarValue(obj.label);
  }
  return '';
}

function resolveGroupChildValues(
  parameterValues: Record<string, unknown>,
  groupParamName: string,
  child: any,
): Array<{ value: unknown; rowIndex: number | null }> {
  const childKey = String(child?.parameterName || child?.safeKey || child?.name || child?.label || '').trim();
  if (!childKey) return [];

  const directValue = parameterValues[childKey];
  if (directValue !== null && directValue !== undefined) {
    return [{ value: directValue, rowIndex: null }];
  }

  const groupValue = parameterValues[groupParamName];
  if (!groupValue) return [];

  if (Array.isArray(groupValue)) {
    return groupValue
      .map((row, rowIndex) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) {
          return { value: undefined, rowIndex };
        }
        const rowObj = row as Record<string, unknown>;
        return {
          value: rowObj[childKey] ?? rowObj[child.safeKey] ?? rowObj[child.name] ?? rowObj[child.label],
          rowIndex,
        };
      })
      .filter((entry) => entry.value !== null && entry.value !== undefined);
  }

  if (typeof groupValue === 'object' && !Array.isArray(groupValue)) {
    const rowObj = groupValue as Record<string, unknown>;
    return [
      {
        value: rowObj[childKey] ?? rowObj[child.safeKey] ?? rowObj[child.name] ?? rowObj[child.label],
        rowIndex: null,
      },
    ].filter((entry) => entry.value !== null && entry.value !== undefined);
  }

  return [];
}

function isMissingMandatoryScalar(raw: unknown): boolean {
  if (raw === null || raw === undefined) return true;
  if (typeof raw === 'string') return raw.trim() === '';
  if (typeof raw === 'number') return !Number.isFinite(raw);
  if (Array.isArray(raw)) return raw.length === 0;
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (obj.amount !== undefined) return isMissingMandatoryScalar(obj.amount);
    if (obj.value !== undefined) return isMissingMandatoryScalar(obj.value);
    if (obj.input !== undefined) return isMissingMandatoryScalar(obj.input);
    if (obj.label !== undefined) return isMissingMandatoryScalar(obj.label);
    return false;
  }
  return false;
}

function isMissingMandatoryFieldValue(value: unknown, isUnitsField: boolean): boolean {
  if (!isUnitsField) {
    return isMissingMandatoryScalar(value);
  }

  if (value === null || value === undefined) return true;
  if (typeof value === 'string' || typeof value === 'number') {
    return isMissingMandatoryScalar(value);
  }
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value !== 'object') return true;

  const obj = value as Record<string, unknown>;
  const unit = String(obj.unit ?? obj.units ?? '').trim();
  const amount = obj.amount ?? obj.value ?? obj.input ?? obj.label;

  if (!unit) return true;
  return isMissingMandatoryScalar(amount);
}

/**
 * Validate all range parameters against their min/max bounds
 * Returns error message if validation fails, null if all pass
 */
function validateRangeParameters(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[]
): string | null {
  const errors: string[] = [];

  parameterDefs.forEach((def: any) => {
    const paramName = def.parameterName;

    if (def.fieldType === 'range' && paramName) {
      const value = parameterValues[paramName];

      if (value === null || value === undefined || value === '') {
        return;
      }

      const scalar = extractRangeScalarValue(value);
      if (scalar === '') {
        return;
      }

      const numValue = typeof scalar === 'number' ? scalar : Number(scalar);
      if (!Number.isFinite(numValue)) {
        return;
      }

      const min = def.rangeMin;
      const max = def.rangeMax;

      if (min != null && numValue < min) {
        errors.push(`${paramName}: value ${numValue} is below minimum (${min})`);
      }
      if (max != null && numValue > max) {
        errors.push(`${paramName}: value ${numValue} is above maximum (${max})`);
      }
    }

    const children = def.paramValues || def.children || def.subMenu || [];
    if (Array.isArray(children)) {
      children.forEach((child: any) => {
        if (child.fieldType === 'range') {
          const childParamName = child.parameterName;
          
          // For group fields, the value can be stored in two ways:
          // 1. Under the group object: parameterValues[groupName] = { childName: value }
          // 2. Directly by full path: parameterValues[groupName.childName]
          
          let childValue: unknown = undefined;

          // Prefer group-scoped values first to avoid collisions with top-level params
          if (paramName) {
            const groupValueRaw = parameterValues[paramName];
            const groupValue = unwrapMandatoryValue(groupValueRaw);
            if (groupValue && typeof groupValue === 'object' && !Array.isArray(groupValue)) {
              childValue = (groupValue as Record<string, unknown>)[childParamName] ??
                           (groupValue as Record<string, unknown>)[child.safeKey] ??
                           (groupValue as Record<string, unknown>)[child.name] ??
                           (groupValue as Record<string, unknown>)[child.label];
            }
          }

          // Legacy fallback: fully-qualified group path only
          if ((childValue === null || childValue === undefined) && paramName) {
            childValue = parameterValues[`${paramName}.${childParamName}`];
          }
          
          if (childValue === null || childValue === undefined || childValue === '') {
            return;
          }

          const scalar = extractRangeScalarValue(childValue);
          if (scalar === '') {
            return;
          }

          const numValue = typeof scalar === 'number' ? scalar : Number(scalar);
          if (!Number.isFinite(numValue)) {
            return;
          }

          const childMin = child.rangeMin;
          const childMax = child.rangeMax;
          const displayName = `${paramName}.${childParamName}`;

          if (childMin != null && numValue < childMin) {
            errors.push(`${displayName}: value ${numValue} is below minimum (${childMin})`);
          }
          if (childMax != null && numValue > childMax) {
            errors.push(`${displayName}: value ${numValue} is above maximum (${childMax})`);
          }
        }
      });
    }
  });

  return errors.length > 0 ? errors.join('\n') : null;
}

/**
 * Validate all mandatory parameters are filled
 * Returns error message if validation fails, null if all pass
 */
function validateMandatoryParameters(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[]
): string | null {
  const errors = collectMandatoryValidationErrors(parameterValues, parameterDefs);
  return errors.length > 0 ? errors.map((err) => err.message).join('\n') : null;
}

function validateRequiredDuration(
  operationType: OperationTypeDefinition,
  durationSeconds: number,
): string | null {
  const isAllowed = operationType.durationAllow !== false;
  if (!isAllowed) return null;
  return durationSeconds >= 0 ? null : 'Duration must be zero or greater';
}

function findFirstMandatoryValidationTarget(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[],
): StepValidationFocusTarget | null {
  const firstError = collectMandatoryValidationErrors(parameterValues, parameterDefs)[0];
  if (firstError) {
    return {
      kind: 'parameter',
      parameterName: firstError.parameterName,
      fieldPath: firstError.fieldPath,
    };
  }
  return null;
}

type MandatoryValidationError = {
  parameterName: string;
  fieldPath: string;
  message: string;
};

function unwrapMandatoryValue(raw: unknown, depth = 0): unknown {
  if (depth > 10) return raw;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown>;
  if ((obj.parameterId != null || obj.parameterName != null) && obj.value !== undefined) {
    return unwrapMandatoryValue(obj.value, depth + 1);
  }
  return raw;
}

function readFirstDefined(obj: Record<string, unknown>, keys: Array<string | undefined>): unknown {
  for (const key of keys) {
    if (!key) continue;
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }
  }
  return undefined;
}

function isEmptyScalar(raw: unknown): boolean {
  const value = unwrapMandatoryValue(raw);
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return !Number.isFinite(value);
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function usesUnitPair(def: any): boolean {
  const tags = Array.isArray(def?.parameterTags) ? def.parameterTags : [];
  return def?.fieldType === 'units' || tags.some((tag: unknown) => String(tag || '').trim() !== '' && String(tag).toLowerCase() !== 'none');
}

function readUnitAndAmount(raw: unknown): { amount: unknown; unit: string } {
  const value = unwrapMandatoryValue(raw);

  if (value == null) return { amount: '', unit: '' };
  if (typeof value === 'string' || typeof value === 'number') {
    return { amount: value, unit: '' };
  }

  if (typeof value !== 'object' || Array.isArray(value)) {
    return { amount: '', unit: '' };
  }

  const obj = value as Record<string, unknown>;
  const nested =
    (obj.amount && typeof obj.amount === 'object' && !Array.isArray(obj.amount)
      ? (obj.amount as Record<string, unknown>)
      : null) ??
    (obj.value && typeof obj.value === 'object' && !Array.isArray(obj.value)
      ? (obj.value as Record<string, unknown>)
      : null);

  if (nested) {
    return {
      amount: nested.amount ?? nested.value ?? '',
      unit: String(nested.unit ?? nested.units ?? obj.unit ?? obj.units ?? '').trim(),
    };
  }

  return {
    amount: obj.amount ?? obj.value ?? '',
    unit: String(obj.unit ?? obj.units ?? '').trim(),
  };
}

function collectMandatoryValidationErrors(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[],
): MandatoryValidationError[] {
  const errors: MandatoryValidationError[] = [];
  const topLevelParamNames = new Set(
    parameterDefs
      .map((def: any) => String(def?.parameterName || def?.safeKey || def?.id || '').trim())
      .filter(Boolean),
  );

  parameterDefs.forEach((def: any) => {
    const paramName = String(def?.parameterName || def?.safeKey || def?.id || '').trim();
    if (!paramName) return;

    const required = Boolean(def?.required || def?.mandatory);
    const groupChildren = Array.isArray(def?.children)
      ? def.children
      : Array.isArray(def?.paramValues)
        ? def.paramValues
        : Array.isArray(def?.subMenu)
          ? def.subMenu
          : [];

    const rootValue = parameterValues[paramName];
    const normalizedRootValue = unwrapMandatoryValue(rootValue);

    if (required) {
      if (usesUnitPair(def)) {
        const pair = readUnitAndAmount(normalizedRootValue);
        if (isEmptyScalar(pair.amount)) {
          errors.push({
            parameterName: paramName,
            fieldPath: paramName,
            message: `${paramName} is required`,
          });
        } else if (!pair.unit) {
          errors.push({
            parameterName: paramName,
            fieldPath: paramName,
            message: `${paramName} unit is required`,
          });
        }
      } else if (isEmptyScalar(normalizedRootValue)) {
        errors.push({
          parameterName: paramName,
          fieldPath: paramName,
          message: `${paramName} is required`,
        });
      }
    }

    if (groupChildren.length === 0) return;

    const isMultipleGroup = Boolean(def?.allowMultiple);
    const groupRows = isMultipleGroup
      ? (Array.isArray(normalizedRootValue)
        ? normalizedRootValue
        : normalizedRootValue && typeof normalizedRootValue === 'object'
          ? [normalizedRootValue as Record<string, unknown>]
          : [])
      : [];
    const groupObject = !isMultipleGroup && normalizedRootValue && typeof normalizedRootValue === 'object' && !Array.isArray(normalizedRootValue)
      ? (normalizedRootValue as Record<string, unknown>)
      : null;

    groupChildren.forEach((child: any) => {
      if (!child?.required && !child?.mandatory) return;

      const childKey = String(child?.safeKey || child?.parameterName || child?.id || '').trim();
      if (!childKey) return;

      const resolveChildValue = (container?: Record<string, unknown> | null): unknown => {
        if (container) {
          const fromContainer = readFirstDefined(container, [childKey, child?.parameterName, child?.name, child?.label]);
          if (fromContainer !== undefined) return fromContainer;
        }

        // Avoid reading top-level parameter values when child names collide
        const childParamName = String(child?.parameterName || '').trim();
        const hasTopLevelCollision = topLevelParamNames.has(childKey) || (childParamName && topLevelParamNames.has(childParamName));
        const fromFlat = hasTopLevelCollision
          ? readFirstDefined(parameterValues, [`${paramName}.${childKey}`])
          : readFirstDefined(parameterValues, [`${paramName}.${childKey}`, childKey, child?.parameterName]);
        return fromFlat;
      };

      const pushMissing = (fieldPath: string, message: string) => {
        errors.push({
          parameterName: paramName,
          fieldPath,
          message,
        });
      };

      if (isMultipleGroup && groupRows.length > 0) {
        groupRows.forEach((row, rowIndex) => {
          const childValue = resolveChildValue((row ?? {}) as Record<string, unknown>);
          const childPath = `${paramName}[${rowIndex}].${childKey}`;
          if (usesUnitPair(child)) {
            const pair = readUnitAndAmount(childValue);
            if (isEmptyScalar(pair.amount)) {
              pushMissing(childPath, `${childPath} is required`);
            } else if (!pair.unit) {
              pushMissing(childPath, `${childPath} unit is required`);
            }
            return;
          }
          if (isEmptyScalar(childValue)) {
            pushMissing(childPath, `${childPath} is required`);
          }
        });
        return;
      }

      const childValue = resolveChildValue(groupObject);
      const childPath = `${paramName}.${childKey}`;
      if (usesUnitPair(child)) {
        const pair = readUnitAndAmount(childValue);
        if (isEmptyScalar(pair.amount)) {
          pushMissing(childPath, `${childPath} is required`);
        } else if (!pair.unit) {
          pushMissing(childPath, `${childPath} unit is required`);
        }
        return;
      }

      if (isEmptyScalar(childValue)) {
        pushMissing(childPath, `${childPath} is required`);
      }
    });
  });

  return errors;
}

function isUniqueSupportedFieldType(fieldType: string): boolean {
  return fieldType === 'text' || fieldType === 'textNumber' || fieldType === 'number';
}

function unwrapUniqueValue(raw: unknown, depth = 0): unknown {
  if (depth > 8) return raw;
  if (raw == null) return '';
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if ((obj.parameterId != null || obj.parameterName != null) && obj.value !== undefined) {
      return unwrapUniqueValue(obj.value, depth + 1);
    }
    if (obj.amount != null || obj.unit != null || obj.units != null) {
      const amount = unwrapUniqueValue(obj.amount ?? '');
      const unit = String(obj.unit ?? obj.units ?? '').trim();
      if (amount === '' && !unit) return '';
      return `${String(amount).trim()}|${unit}`;
    }
    if (obj.value != null) return unwrapUniqueValue(obj.value, depth + 1);
    if (obj.label != null) return unwrapUniqueValue(obj.label, depth + 1);
    if (obj.input != null) return unwrapUniqueValue(obj.input, depth + 1);
  }
  return '';
}

function normalizeUniqueComparable(raw: unknown): string {
  const unwrapped = unwrapUniqueValue(raw);
  if (typeof unwrapped === 'number') return String(unwrapped);
  if (typeof unwrapped === 'string') return unwrapped.trim();
  return '';
}

type UniqueCandidate = {
  key: string;
  fieldPath: string;
  paramName: string;
  value: string;
};

function getChildUniqueKey(child: any): string {
  return String(child?.safeKey || child?.parameterName || child?.id || '').trim();
}

function collectUniqueCandidates(
  parameterValues: Record<string, unknown>,
  parameterDefs: any[],
): UniqueCandidate[] {
  const out: UniqueCandidate[] = [];

  parameterDefs.forEach((def: any) => {
    const paramName = String(def.parameterName || def.id || '').trim();
    if (!paramName) return;

    if (def.uniqueAcrossWorkspace && isUniqueSupportedFieldType(def.fieldType)) {
      const value = normalizeUniqueComparable(parameterValues[paramName]);
      if (value) {
        out.push({
          key: paramName,
          fieldPath: paramName,
          paramName,
          value,
        });
      }
    }

    if (def.fieldType !== 'group') return;

    const children = Array.isArray(def.children)
      ? def.children
      : Array.isArray(def.paramValues)
        ? def.paramValues
        : Array.isArray(def.subMenu)
          ? def.subMenu
          : [];

    const groupRaw = parameterValues[paramName];
    const isMultipleGroup = Boolean(def.allowMultiple);
    const groupRows = isMultipleGroup
      ? (Array.isArray(groupRaw)
        ? groupRaw
        : groupRaw && typeof groupRaw === 'object'
          ? [groupRaw as Record<string, unknown>]
          : [])
      : [];

    children.forEach((child: any) => {
      if (!child?.uniqueAcrossWorkspace || !isUniqueSupportedFieldType(child?.fieldType)) return;

      const childName = getChildUniqueKey(child);
      if (!childName) return;

      const childPath = `${paramName}.${childName}`;

      if (isMultipleGroup && groupRows.length > 0) {
        groupRows.forEach((row, rowIndex) => {
          const raw = row?.[childName] ?? row?.[child.safeKey] ?? row?.[child.name] ?? row?.[child.label];
          const value = normalizeUniqueComparable(raw);
          if (!value) return;
          out.push({
            key: `${paramName}[${rowIndex}].${childName}`,
            fieldPath: childPath,
            paramName,
            value,
          });
        });
        return;
      }

      const groupObjectRaw = !isMultipleGroup && groupRaw && typeof groupRaw === 'object' && !Array.isArray(groupRaw)
        ? ((groupRaw as Record<string, unknown>)[childName]
          ?? (groupRaw as Record<string, unknown>)[child.safeKey]
          ?? (groupRaw as Record<string, unknown>)[child.name]
          ?? (groupRaw as Record<string, unknown>)[child.label])
        : undefined;

      const fallbackRaw = groupObjectRaw ?? parameterValues[childName];
      const fallbackValue = normalizeUniqueComparable(fallbackRaw);
      if (fallbackValue) {
        out.push({
          key: childPath,
          fieldPath: childPath,
          paramName,
          value: fallbackValue,
        });
      }
    });
  });

  return out;
}

function formatUniqueConflictMessage(entity: Record<string, unknown> | undefined): string {
  const workflowName =
    String(entity?.workflowName ?? entity?.name ?? entity?.dataFileName ?? '').trim() || 'Unknown workflow';

  const version =
    entity?.versionNumber ?? entity?.version ?? entity?.workflowVersion ?? entity?.operationVersion;
  const versionText = version != null && String(version).trim() !== '' ? `v${String(version).trim()}` : 'unknown version';
  const stepName = String(entity?.stepName ?? entity?.operationName ?? entity?.displayName ?? '').trim() || 'Unknown step';

  return `This value is already used in workflow "${workflowName}" (${versionText}), step "${stepName}". Please enter a unique value.`;
}

function getParamNameFromUniqueKey(key: string): string {
  const beforeRow = key.split('[')[0];
  return beforeRow.split('.')[0] || beforeRow;
}

export function AddStepModal({ sourceMaterialId, sourceMaterialIds, archetypes, mode = 'add', onClose, onSaved }: AddStepModalProps) {
  const { data: catalog, isLoading } = useOperationCatalog();
  const addStep = useGraphEditorStore((s) => s.addStep);
  const addStepBatch = useGraphEditorStore((s) => s.addStepBatch);
  const insertStep = useGraphEditorStore((s) => s.insertStep);
  const addTransferStep = useGraphEditorStore((s) => s.addTransferStep);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);
  const [form, setForm] = useState<AddStepFormState>(INITIAL_FORM_STATE);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uniqueFieldErrors, setUniqueFieldErrors] = useState<Record<string, string>>({});
  const [uniqueFieldPending, setUniqueFieldPending] = useState<Record<string, boolean>>({});
  const [validationFocusTarget, setValidationFocusTarget] = useState<StepValidationFocusTarget | null>(null);
  const [validationFocusRequestId, setValidationFocusRequestId] = useState(0);
  const [transferInvalidDestinationRowIds, setTransferInvalidDestinationRowIds] = useState<string[]>([]);
  const [transferDestinationValidationRequestId, setTransferDestinationValidationRequestId] = useState(0);
  const transferFormRef = useRef<TransferFormState | null>(null);
  const [transferPreviewForm, setTransferPreviewForm] = useState<TransferFormState | null>(null);
  const complexFormRef = useRef<{ form: ComplexFormState; valid: boolean }>({ form: null!, valid: false });
  const [complexPreviewAdditionalInputIds, setComplexPreviewAdditionalInputIds] = useState<string[]>([]);
  const addComplexStep = useGraphEditorStore((s) => s.addComplexStep);
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  const normalizedSourceMaterialIds = useMemo(() => {
    const ids = sourceMaterialIds?.length ? sourceMaterialIds : [sourceMaterialId];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  }, [sourceMaterialId, sourceMaterialIds]);
  const isBatchSelectionMode = mode === 'add' && normalizedSourceMaterialIds.length > 1;
  const sourceMaterial = useMemo(
    () => materialNodes[sourceMaterialId],
    [materialNodes, sourceMaterialId],
  );
  const selectedSourceMaterials = useMemo(
    () => normalizedSourceMaterialIds.map((id) => materialNodes[id]).filter(Boolean),
    [materialNodes, normalizedSourceMaterialIds],
  );
  const uniqueValidationSequenceRef = useRef(0);
  const previousUniqueValueByKeyRef = useRef<Map<string, string>>(new Map());

  const selectedOp: OperationTypeDefinition | null = useMemo(
    () => catalog?.find((o) => o.id === form.selectedOperationTypeId) ?? null,
    [catalog, form.selectedOperationTypeId],
  );
  const isBatchAddMode = isBatchSelectionMode && selectedOp?.operationType !== 'COMPLEX';
  useEffect(() => {
    const currentSeq = uniqueValidationSequenceRef.current + 1;
    uniqueValidationSequenceRef.current = currentSeq;

    if (!selectedOp || selectedOp.operationType === 'TRANSFER') {
      previousUniqueValueByKeyRef.current = new Map();
      setUniqueFieldErrors({});
      setUniqueFieldPending({});
      return;
    }

    const parameterDefs = selectedOp.parameters ?? [];
    const candidates = collectUniqueCandidates(form.parameterValues, parameterDefs);

    if (candidates.length === 0) {
      previousUniqueValueByKeyRef.current = new Map();
      setUniqueFieldErrors({});
      setUniqueFieldPending({});
      return;
    }

    const nextErrors: Record<string, string> = {};

    const currentValueByKey = new Map<string, string>();
    candidates.forEach((candidate) => {
      currentValueByKey.set(candidate.key, candidate.value);
    });

    const previousValueByKey = previousUniqueValueByKeyRef.current;
    const changedCandidateKeys = new Set<string>();
    currentValueByKey.forEach((value, key) => {
      if (previousValueByKey.get(key) !== value) {
        changedCandidateKeys.add(key);
      }
    });

    const removedCandidateKeys = new Set<string>();
    previousValueByKey.forEach((_, key) => {
      if (!currentValueByKey.has(key)) {
        changedCandidateKeys.add(key);
        removedCandidateKeys.add(key);
      }
    });

    previousUniqueValueByKeyRef.current = currentValueByKey;

    for (const [key, isPending] of Object.entries(uniqueFieldPending)) {
      if (isPending) changedCandidateKeys.add(key);
    }

    const candidatesToEvaluate = candidates.filter((candidate) => changedCandidateKeys.has(candidate.key));

    const localFieldValueCount = new Map<string, number>();
    candidates.forEach((candidate) => {
      const localKey = `${candidate.fieldPath}::${candidate.value.toLowerCase()}`;
      localFieldValueCount.set(localKey, (localFieldValueCount.get(localKey) ?? 0) + 1);
    });

    const localGraphConflictByKey = new Map<string, string>();
    Object.values(operationNodes).forEach((op) => {
      if (!op || op.operationTypeId !== selectedOp.id) return;
      if (!op.parameters || typeof op.parameters !== 'object') return;
      const stepName =
        String(op.displayName ?? op.operationTemplate?.operationTypeId ?? op.operationTypeId ?? '').trim() ||
        'Unknown step';
      const opCandidates = collectUniqueCandidates(op.parameters as Record<string, unknown>, parameterDefs);
      opCandidates.forEach((candidate) => {
        const conflictKey = `${candidate.fieldPath}::${candidate.value.toLowerCase()}`;
        if (!localGraphConflictByKey.has(conflictKey)) {
          localGraphConflictByKey.set(conflictKey, stepName);
        }
      });
    });

    const currentStepName = String(selectedOp?.operationName ?? selectedOp?.id ?? '').trim() || 'Unknown step';

    candidatesToEvaluate.forEach((candidate) => {
      const localKey = `${candidate.fieldPath}::${candidate.value.toLowerCase()}`;
      if ((localFieldValueCount.get(localKey) ?? 0) > 1) {
        nextErrors[candidate.key] = `This value is duplicated in current step "${currentStepName}". Please enter a unique value.`;
        return;
      }
      const conflictingStep = localGraphConflictByKey.get(localKey);
      if (conflictingStep) {
        nextErrors[candidate.key] = `This value is already used in step "${conflictingStep}" in the current graph. Please enter a unique value.`;
      }
    });

    const candidatesForApi = candidatesToEvaluate.filter((candidate) => !nextErrors[candidate.key]);

    setUniqueFieldErrors((prev) => {
      const merged = { ...prev };
      removedCandidateKeys.forEach((key) => delete merged[key]);
      candidatesToEvaluate.forEach((candidate) => delete merged[candidate.key]);
      return { ...merged, ...nextErrors };
    });

    setUniqueFieldPending((prev) => {
      const merged = { ...prev };
      removedCandidateKeys.forEach((key) => delete merged[key]);
      candidatesToEvaluate.forEach((candidate) => delete merged[candidate.key]);
      candidatesForApi.forEach((candidate) => {
        merged[candidate.key] = true;
      });
      return merged;
    });

    if (candidatesForApi.length === 0) {
      return;
    }

    const timeout = setTimeout(async () => {
      const apiErrors: Record<string, string> = {};
      const completed: Record<string, boolean> = {};

      for (const candidate of candidatesForApi) {
        if (uniqueValidationSequenceRef.current !== currentSeq) return;

        try {
          const res = await validateUniqueField({
            fieldName: candidate.fieldPath,
            fieldValue: candidate.value,
            entityType: 'CUSTOM_OPERATION',
            entityId: null,
            formType: 'detail',
            requestType: null,
            formTemplateId: selectedOp.id,
          });

          if (res?.exists) {
            apiErrors[candidate.key] = formatUniqueConflictMessage(res.entity);
          }
        } catch {
          // Do not block user input on transient network failures.
        } finally {
          completed[candidate.key] = true;
        }
      }

      if (uniqueValidationSequenceRef.current !== currentSeq) return;

      setUniqueFieldErrors((prev) => {
        const merged = { ...prev };
        removedCandidateKeys.forEach((key) => delete merged[key]);
        candidatesToEvaluate.forEach((candidate) => delete merged[candidate.key]);
        return { ...merged, ...nextErrors, ...apiErrors };
      });
      setUniqueFieldPending((prev) => {
        const next = { ...prev };
        Object.keys(completed).forEach((key) => {
          next[key] = false;
        });
        return next;
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [form.parameterValues, operationNodes, selectedOp]);

  const handleSelect = useCallback((op: OperationTypeDefinition) => {
    setComplexPreviewAdditionalInputIds([]);
    setValidationFocusTarget(null);
    previousUniqueValueByKeyRef.current = new Map();
    setUniqueFieldErrors({});
    setUniqueFieldPending({});
    setForm({ ...INITIAL_FORM_STATE, selectedOperationTypeId: op.id, checkpoints: op.checkpoints ?? [] });
  }, []);

  const handleChange = useCallback((patch: Partial<AddStepFormState>) => {
    setValidationFocusTarget(null);
    setForm((prev) => {
      const normalizedPatch = { ...patch };
      if (Array.isArray(normalizedPatch.recommendedInstrument)) {
        normalizedPatch.recommendedInstrument = normalizedPatch.recommendedInstrument.slice(0, MAX_RECOMMENDED_INSTRUMENTS);
      }
      return { ...prev, ...normalizedPatch };
    });
  }, []);

  const handleParamChange = useCallback((key: string, value: unknown) => {
    setValidationFocusTarget(null);
    setForm((prev) => ({
      ...prev,
      parameterValues: { ...prev.parameterValues, [key]: value },
    }));
  }, []);

  const focusOnInvalidField = useCallback((target: StepValidationFocusTarget) => {
    setSaveError(null);
    setValidationFocusTarget(target);
    setValidationFocusRequestId((prev) => prev + 1);
  }, []);

  const hasUniquenessErrors = Object.keys(uniqueFieldErrors).length > 0;
  const hasUniquenessPending = Object.values(uniqueFieldPending).some(Boolean);

  const focusFirstUniquenessError = useCallback(() => {
    const firstKey = Object.keys(uniqueFieldErrors)[0];
    if (!firstKey) return;
    const parameterName = getParamNameFromUniqueKey(firstKey);
    if (!parameterName) return;
    focusOnInvalidField({ kind: 'parameter', parameterName });
  }, [focusOnInvalidField, uniqueFieldErrors]);

  const triggerTransferDestinationValidation = useCallback((rows: TransferFormState['rows']) => {
    const invalidIds = rows
      .filter((row) => !row.destinationMaterialId && !row.destinationDraft)
      .map((row) => row.id);

    if (invalidIds.length === 0) {
      setTransferInvalidDestinationRowIds([]);
      return false;
    }

    setSaveError(null);
    setTransferInvalidDestinationRowIds(invalidIds);
    setTransferDestinationValidationRequestId((prev) => prev + 1);
    return true;
  }, []);

  const handleComplexFormChange = useCallback((complexForm: ComplexFormState, valid: boolean) => {
    setValidationFocusTarget(null);
    complexFormRef.current = { form: complexForm, valid };

    setComplexPreviewAdditionalInputIds((prev) =>
      sameStringArray(prev, complexForm.additionalInputIds ?? [])
        ? prev
        : [...(complexForm.additionalInputIds ?? [])],
    );

    setForm((prev) => {
      const next = {
        ...prev,
        parameterValues: complexForm.parameterValues,
        durationHours: complexForm.durationHours,
        durationMinutes: complexForm.durationMinutes,
        durationSeconds: complexForm.durationSeconds,
        linkToPreviousStep: complexForm.linkToPreviousStep,
        linkedStepId: complexForm.linkedStepId,
        notes: complexForm.notes,
        recommendedInstrument: complexForm.recommendedInstrument,
        checkpoints: complexForm.checkpoints,
      };

      const unchanged =
        prev.parameterValues === next.parameterValues &&
        prev.durationHours === next.durationHours &&
        prev.durationMinutes === next.durationMinutes &&
        prev.durationSeconds === next.durationSeconds &&
        prev.linkToPreviousStep === next.linkToPreviousStep &&
        prev.linkedStepId === next.linkedStepId &&
        prev.notes === next.notes &&
        prev.recommendedInstrument === next.recommendedInstrument &&
        prev.checkpoints === next.checkpoints;

      return unchanged ? prev : next;
    });
  }, []);

  const handleComplexSave = useCallback(() => {
    if (!selectedOp) return;

    if (hasUniquenessPending) {
      return;
    }

    if (hasUniquenessErrors) {
      focusFirstUniquenessError();
      return;
    }

    setSaveError(null);
    const cf = complexFormRef.current.form;

    const complexDurationError = validateRequiredDuration(
      selectedOp,
      cf.durationHours * 3600 + cf.durationMinutes * 60 + cf.durationSeconds,
    );
    if (complexDurationError) {
      setSaveError(complexDurationError);
      return;
    }

    // Validate mandatory parameters
    const mandatoryTarget = findFirstMandatoryValidationTarget(
      cf.parameterValues,
      selectedOp.parameters ?? []
    );
    if (mandatoryTarget) {
      focusOnInvalidField(mandatoryTarget);
      return;
    }

    // Validate range parameters
    const rangeValidationError = validateRangeParameters(
      cf.parameterValues,
      selectedOp.parameters ?? []
    );
    if (rangeValidationError) {
      setSaveError(`Range parameter validation failed: ${rangeValidationError}`);
      return;
    }

    const duration: Duration | undefined =
      selectedOp.durationAllow
        ? {
            isInfinity: false,
            value: cf.durationHours * 3600 + cf.durationMinutes * 60 + cf.durationSeconds,
            linkToPreviousStep: cf.linkToPreviousStep,
            prevStep: cf.linkedStepId ?? undefined,
          }
        : undefined;
    try {
      // Build operationTemplate for COMPLEX
      const operationTemplate = buildOperationTemplate(selectedOp, {
        ...INITIAL_FORM_STATE,
        selectedOperationTypeId: selectedOp.id,
        parameterValues: cf.parameterValues,
      });

      // Generate wrapped parameter format using formToOperationSpec for consistency with TRANSFORM
      // Parameters must be wrapped as { parameterId, parameterName, value } for backend storage
      const wrappedSpec = formToOperationSpec(
        {
          ...INITIAL_FORM_STATE,
          selectedOperationTypeId: selectedOp.id,
          parameterValues: cf.parameterValues,
          durationHours: cf.durationHours,
          durationMinutes: cf.durationMinutes,
          durationSeconds: cf.durationSeconds,
          linkToPreviousStep: cf.linkToPreviousStep,
          linkedStepId: cf.linkedStepId,
          notes: cf.notes,
          recommendedInstrument: cf.recommendedInstrument,
          checkpoints: cf.checkpoints,
        },
        selectedOp,
      );

      const input: AddComplexStepInput = {
        sourceMaterialId,
        operationTypeId: selectedOp.id,
        operationDisplayName: selectedOp.operationName,
        additionalInputIds: cf.additionalInputIds,
        outputMaterialIds: cf.outputMaterialIds,
        ioMapping: cf.ioMapping ?? [],
        parameters: wrappedSpec.parameters,
        duration,
        notes: cf.notes || form?.notes || undefined,
        recommendedInstrument: cf.recommendedInstrument || undefined,
        checkpoints: cf.checkpoints || [],
        approvers: form?.approvers,
        notifications: form?.notifications,
        operationTemplate,
      };
      const result = addComplexStep(input);
      onSaved?.(result.operationId, '', {
        operationTemplate,
        operationTypeId: selectedOp.id,
        stepVertexId: result.operationId,
      });
      onClose();
    } catch (err) {
      setSaveError(String(err));
    }
  }, [selectedOp, hasUniquenessPending, hasUniquenessErrors, focusFirstUniquenessError, sourceMaterialId, addComplexStep, onSaved, onClose, form.approvers, form.notifications, form.notes]);

  const handleSave = () => {
    if (!selectedOp) return;

    if (selectedOp.operationType !== 'TRANSFER') {
      if (hasUniquenessPending) {
        return;
      }
      if (hasUniquenessErrors) {
        focusFirstUniquenessError();
        return;
      }
    }

    setSaveError(null);

    if (mode === 'insert') {
      // Insert mode: all archetypes use insertStep to ensure downstream rewiring
      try {
        if (selectedOp.operationType === 'TRANSFER') {
          const tf = transferFormRef.current;
          if (!tf || !tf.rows[0]) return;
          if (triggerTransferDestinationValidation(tf.rows)) {
            return;
          }
          const row = tf.rows[0];

          const transferDurationError = validateRequiredDuration(
            selectedOp,
            tf.durationHours * 3600 + tf.durationMinutes * 60 + tf.durationSeconds,
          );
          if (transferDurationError) {
            setSaveError(transferDurationError);
            return;
          }
          
          // Validate mandatory parameters for TRANSFER (typically none, but check anyway)
          const mandatoryError = validateMandatoryParameters(
            {},
            selectedOp.parameters ?? []
          );
          if (mandatoryError) {
            setSaveError(`Mandatory parameter validation failed:\n${mandatoryError}`);
            return;
          }
          
          // Build operationTemplate for TRANSFER
          const operationTemplate = buildOperationTemplate(selectedOp, {
            ...INITIAL_FORM_STATE,
            selectedOperationTypeId: selectedOp.id,
            parameterValues: {},
          });
          const result = insertStep({
            anchorMaterialId: sourceMaterialId,
            archetype: 'TRANSFER',
            displayName: selectedOp.operationName,
            parameters: {},
            checkpoints: tf.checkpoints || [],
            duration: selectedOp.durationAllow
              ? { isInfinity: false, value: tf.durationHours * 3600 + tf.durationMinutes * 60 + tf.durationSeconds, linkToPreviousStep: tf.linkToPreviousStep, prevStep: tf.linkedStepId ?? undefined }
              : undefined,
            operationTemplate,
            transferConfig: {
              anchorRole: 'source',
              secondMaterialId: row.destinationMaterialId ?? undefined,
              createNewMaterial: row.destinationDraft
                ? { name: row.destinationDraft.name, containerName: row.destinationDraft.container.name, containerType: row.destinationDraft.container.containerType }
                : undefined,
              quantity: { value: row.amount ?? 0, unit: (row.unit ?? 'mL') as string },
            },
          });
          onSaved?.(result.operationId, result.rewireTargetId, {
            operationTemplate,
            operationTypeId: selectedOp.id,
            stepVertexId: result.operationId,
          });
        } else if (selectedOp.operationType === 'COMPLEX') {
          const cf = complexFormRef.current;
          if (!cf.valid) return;

          const complexDurationError = validateRequiredDuration(
            selectedOp,
            cf.form.durationHours * 3600 + cf.form.durationMinutes * 60 + cf.form.durationSeconds,
          );
          if (complexDurationError) {
            setSaveError(complexDurationError);
            return;
          }
          
          // Validate mandatory parameters for COMPLEX
          const mandatoryTarget = findFirstMandatoryValidationTarget(
            cf.form.parameterValues,
            selectedOp.parameters ?? []
          );
          if (mandatoryTarget) {
            focusOnInvalidField(mandatoryTarget);
            return;
          }
          
          // Validate range parameters for COMPLEX
          const rangeValidationError = validateRangeParameters(
            cf.form.parameterValues,
            selectedOp.parameters ?? []
          );
          if (rangeValidationError) {
            setSaveError(`Range parameter validation failed: ${rangeValidationError}`);
            return;
          }
          
          // Build operationTemplate for COMPLEX
          const operationTemplate = buildOperationTemplate(selectedOp, {
            ...INITIAL_FORM_STATE,
            selectedOperationTypeId: selectedOp.id,
            parameterValues: cf.form.parameterValues,
          });
          const wrappedSpec = formToOperationSpec(
            {
              ...INITIAL_FORM_STATE,
              selectedOperationTypeId: selectedOp.id,
              parameterValues: cf.form.parameterValues,
              durationHours: cf.form.durationHours,
              durationMinutes: cf.form.durationMinutes,
              durationSeconds: cf.form.durationSeconds,
              linkToPreviousStep: cf.form.linkToPreviousStep,
              linkedStepId: cf.form.linkedStepId,
              notes: cf.form.notes,
              recommendedInstrument: [],
              checkpoints: cf.form.checkpoints,
            },
            selectedOp,
          );
          const result = insertStep({
            anchorMaterialId: sourceMaterialId,
            archetype: 'COMPLEX',
            displayName: selectedOp.operationName,
            parameters: wrappedSpec.parameters,
            checkpoints: cf.form.checkpoints || [],
            duration: selectedOp.durationAllow
              ? { isInfinity: false, value: cf.form.durationHours * 3600 + cf.form.durationMinutes * 60 + cf.form.durationSeconds, linkToPreviousStep: cf.form.linkToPreviousStep, prevStep: cf.form.linkedStepId ?? undefined }
              : undefined,
            operationTemplate,
            complexConfig: {
              additionalInputs: cf.form.additionalInputIds,
              outputMaterialIds: cf.form.outputMaterialIds,
            },
          });
          onSaved?.(result.operationId, result.rewireTargetId, {
            operationTemplate,
            operationTypeId: selectedOp.id,
            stepVertexId: result.operationId,
          });
        } else {
          // TRANSFORM / MEASURE
          // Validate mandatory parameters before insert
          const mandatoryTarget = findFirstMandatoryValidationTarget(
            form.parameterValues,
            selectedOp.parameters ?? []
          );
          if (mandatoryTarget) {
            focusOnInvalidField(mandatoryTarget);
            return;
          }

          const durationError = validateRequiredDuration(
            selectedOp,
            form.durationHours * 3600 + form.durationMinutes * 60 + form.durationSeconds,
          );
          if (durationError) {
            focusOnInvalidField({ kind: 'duration' });
            return;
          }
          
          // Validate range parameters before insert
          const rangeValidationError = validateRangeParameters(
            form.parameterValues,
            selectedOp.parameters ?? []
          );
          if (rangeValidationError) {
            setSaveError(`Range parameter validation failed: ${rangeValidationError}`);
            return;
          }

          const operationTemplate = buildOperationTemplate(selectedOp, form);
          const operationSpec = {
            ...formToOperationSpec(form, selectedOp),
            operationTemplate,
          };
          const result = insertStep({
            anchorMaterialId: sourceMaterialId,
            archetype: operationSpec.archetype,
            operationTypeId: operationSpec.operationTypeId,
            displayName: operationSpec.displayName,
            parameters: operationSpec.parameters,
            checkpoints: operationSpec.checkPoints ?? [],
            duration: operationSpec.duration,
            operationTemplate,
          });
          onSaved?.(result.operationId, result.rewireTargetId, {
            operationTemplate,
            operationTypeId: selectedOp.id,
            stepVertexId: result.operationId,
          });
        }
        onClose();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (
          selectedOp.operationType === 'TRANSFER' &&
          message.includes('Either destinationMaterialId or destinationDraft is required')
        ) {
          const tf = transferFormRef.current;
          if (tf?.rows?.length) {
            triggerTransferDestinationValidation(tf.rows);
            return;
          }
        }
        setSaveError(message);
      }
      return;
    }

    // Add mode
    if (selectedOp.operationType === 'TRANSFER') {
      if (transferFormRef.current) {
        handleTransferSave(transferFormRef.current);
      }
      return;
    }
    if (selectedOp.operationType === 'COMPLEX') {
      handleComplexSave();
      return;
    }

    // TRANSFORM / MEASURE — Add mode
    // Validate mandatory parameters before adding
    const mandatoryTarget = findFirstMandatoryValidationTarget(
      form.parameterValues,
      selectedOp.parameters ?? []
    );
    if (mandatoryTarget) {
      focusOnInvalidField(mandatoryTarget);
      return;
    }

    const durationError = validateRequiredDuration(
      selectedOp,
      form.durationHours * 3600 + form.durationMinutes * 60 + form.durationSeconds,
    );
    if (durationError) {
      focusOnInvalidField({ kind: 'duration' });
      return;
    }

    // Validate range parameters before adding
    const rangeValidationError = validateRangeParameters(
      form.parameterValues,
      selectedOp.parameters ?? []
    );
    if (rangeValidationError) {
      setSaveError(`Range parameter validation failed: ${rangeValidationError}`);
      return;
    }

    const operationTemplate = buildOperationTemplate(selectedOp, form);
    const operationSpec = {
      ...formToOperationSpec(form, selectedOp),
      operationTemplate,
    };
    if (isBatchAddMode) {
      const batchResult = addStepBatch({
        sourceMaterialIds: normalizedSourceMaterialIds,
        operationSpec,
      });
      const operationId = batchResult.operationIds[batchResult.operationIds.length - 1];
      const downstreamMaterialId = batchResult.downstreamMaterialIds[batchResult.downstreamMaterialIds.length - 1];
      onSaved?.(operationId, downstreamMaterialId, {
        operationTemplate,
        operationTypeId: selectedOp.id,
        stepVertexId: operationId,
      });
    } else {
      const result = addStep({ sourceMaterialId, operationSpec });
      onSaved?.(result.operationId, result.downstreamMaterialId, {
        operationTemplate,
        operationTypeId: selectedOp.id,
        stepVertexId: result.operationId,
      });
    }
    onClose();
  };

  const handleTransferSave = useCallback(
    (transferForm: TransferFormState) => {
      if (!selectedOp) return;
      setSaveError(null);

      if (triggerTransferDestinationValidation(transferForm.rows)) {
        return;
      }

      const transferDurationError = validateRequiredDuration(
        selectedOp,
        transferForm.durationHours * 3600 + transferForm.durationMinutes * 60 + transferForm.durationSeconds,
      );
      if (transferDurationError) {
        setSaveError(transferDurationError);
        return;
      }

      // Validate mandatory parameters for TRANSFER (typically none, but check anyway)
      const mandatoryError = validateMandatoryParameters(
        {},
        selectedOp.parameters ?? []
      );
      if (mandatoryError) {
        setSaveError(`Mandatory parameter validation failed:\n${mandatoryError}`);
        return;
      }

      const duration: Duration | undefined =
        selectedOp.durationAllow
          ? {
              isInfinity: false,
              value:
                transferForm.durationHours * 3600 +
                transferForm.durationMinutes * 60 +
                transferForm.durationSeconds,
              linkToPreviousStep: transferForm.linkToPreviousStep,
              prevStep: transferForm.linkedStepId ?? undefined,
            }
          : undefined;

      try {
        // Build operationTemplate for TRANSFER
        const operationTemplate = buildOperationTemplate(selectedOp, {
          ...INITIAL_FORM_STATE,
          selectedOperationTypeId: selectedOp.id,
          parameterValues: {},
        });
        
        if (isBatchAddMode) {
          for (const row of transferForm.rows) {
            const destinationId = row.destinationMaterialId ?? undefined;
            if (destinationId && normalizedSourceMaterialIds.includes(destinationId)) {
              setSaveError('Destination cannot overlap selected source materials');
              return;
            }

            addStepBatch({
              sourceMaterialIds: normalizedSourceMaterialIds,
              transferSharedConfig: {
                operationTypeId: selectedOp.id,
                operationDisplayName: selectedOp.operationName,
                row,
                transferOptions: {
                  quantityType: transferForm.quantityType,
                  transferEntireVolume:
                    row.amount === 0 || (row.amount != null && row.amount <= 0)
                      ? false
                      : transferForm.transferEntireVolume,
                  sameVolume: transferForm.sameVolume,
                  advancedOptionsEnabled: false,
                },
                destinationMaterialId: destinationId,
                destinationDraft: row.destinationDraft ?? undefined,
                duration,
                notes: transferForm.notes || form?.notes || undefined,
                recommendedInstrument:
                  transferForm.recommendedInstrument || undefined,
                checkpoints: transferForm.checkpoints || [],
                approvers: form?.approvers,
                notifications: form?.notifications,
                operationTemplate,
              },
            });
          }
        } else {
          for (const row of transferForm.rows) {
            addTransferStep({
              sourceMaterialId,
              operationTypeId: selectedOp.id,
              operationDisplayName: selectedOp.operationName,
              row,
              transferOptions: {
                quantityType: transferForm.quantityType,
                transferEntireVolume:
                  row.amount === 0 || (row.amount != null && row.amount <= 0)
                    ? false
                    : transferForm.transferEntireVolume,
                sameVolume: transferForm.sameVolume,
                advancedOptionsEnabled: false,
              },
              destinationMaterialId: row.destinationMaterialId ?? undefined,
              destinationDraft: row.destinationDraft ?? undefined,
              duration,
              notes: transferForm.notes || form?.notes || undefined,
              recommendedInstrument:
                transferForm.recommendedInstrument || undefined,
              checkpoints: transferForm.checkpoints || [],
              approvers: form?.approvers,
              notifications: form?.notifications,
              operationTemplate,
            });
          }
        }
        onClose();
      } catch (err) {
        const message = String(err);
        if (message.includes('Either destinationMaterialId or destinationDraft is required')) {
          if (triggerTransferDestinationValidation(transferForm.rows)) {
            return;
          }
        }
        setSaveError(message);
      }
    },
    [selectedOp, sourceMaterialId, addTransferStep, addStepBatch, onClose, form.approvers, form.notifications, form.notes, triggerTransferDestinationValidation, isBatchAddMode, normalizedSourceMaterialIds],
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth sx={{ '& .MuiDialog-paper': { height: '80vh' } }}>
      <DialogTitle sx={{ 
        height: 60,
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '18px',
        fontWeight: 600,
        backgroundColor: '#fafbfc',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#204c77',
            }}
          >
            {mode === 'insert' ? 'Insert Step' : 'Add Step'}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </DialogTitle>
      {mode === 'insert' && sourceMaterial && (
        <Box sx={{ px: 3, py: 1, bgcolor: '#eff6ff', borderBottom: '1px solid #bfdbfe' }}>
          <Typography variant="body2" color="primary">
            Inserting step after: <strong>{sourceMaterial.name}</strong>
            {sourceMaterial.container?.name ? ` (${sourceMaterial.container.name})` : ''}
          </Typography>
        </Box>
      )}
      {isBatchAddMode && (
        <Box sx={{ px: 3, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            Adding one step to {selectedSourceMaterials.length} selected materials (in selection order).
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {selectedSourceMaterials.map((material) => (
              <Box
                key={material.id}
                sx={{
                  px: 1,
                  py: 0.35,
                  borderRadius: 1,
                  bgcolor: '#e2e8f0',
                  color: '#0f172a',
                  fontSize: 12,
                }}
              >
                {material.name}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <DialogContent dividers sx={{ display: 'flex', gap: 0, p: 0, overflow: 'hidden' }}>
        {/* Left: Operations Toolbox (22%) */}
        <Box sx={{ width: '22%', borderRight: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <OperationsToolbox
            catalog={catalog ?? []}
            isLoading={isLoading}
            archetypes={archetypes ?? ['TRANSFORM', 'MEASURE', 'TRANSFER', 'COMPLEX']}
            selectedId={form.selectedOperationTypeId}
            onSelect={handleSelect}
          />
        </Box>

        {/* Center: Step Details (50%) */}
        <Box sx={{ width: '50%', borderRight: 1, borderColor: 'divider', overflow: 'hidden' }}>
          {selectedOp?.operationType === 'TRANSFER' ? (
            <TransferDetailsPanel
              sourceMaterialId={sourceMaterialId}
              batchMode={isBatchAddMode}
              excludedDestinationMaterialIds={isBatchAddMode ? normalizedSourceMaterialIds : []}
              operationType={selectedOp}
              onSave={handleTransferSave}
              invalidDestinationRowIds={transferInvalidDestinationRowIds}
              destinationValidationRequestId={transferDestinationValidationRequestId}
              onFormChange={(f) => {
                transferFormRef.current = f;
                setTransferPreviewForm(f);
              }}
            />
          ) : selectedOp?.operationType === 'COMPLEX' ? (
            <ComplexDetailsPanel
              sourceMaterialId={sourceMaterialId}
              operationType={selectedOp}
              onFormChange={handleComplexFormChange}
              validationFocusTarget={validationFocusTarget}
              validationFocusRequestId={validationFocusRequestId}
              uniqueFieldErrors={uniqueFieldErrors}
              uniqueFieldPending={uniqueFieldPending}
            />
          ) : (
            <StepDetailsPanel
              operationType={selectedOp}
              form={form}
              onChange={handleChange}
              onParamChange={handleParamChange}
              validationFocusTarget={validationFocusTarget}
              validationFocusRequestId={validationFocusRequestId}
              uniqueFieldErrors={uniqueFieldErrors}
              uniqueFieldPending={uniqueFieldPending}
            />
          )}
        </Box>

        {/* Right: Side Panel (28%) */}
        <Box sx={{ width: '28%', overflow: 'hidden' }}>
          <StepSidePanel
            operationType={selectedOp}
            form={form}
            onChange={handleChange}
            materialName={sourceMaterial?.name ?? ''}
            operatorName=""
            transferForm={transferPreviewForm}
            sourceMaterialId={sourceMaterialId}
            complexAdditionalInputIds={complexPreviewAdditionalInputIds}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {saveError && (
          <Box sx={{ flex: 1, color: 'error.main', fontSize: 12, pl: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '400px' }}>
            {saveError}
          </Box>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedOp || (selectedOp.operationType !== 'TRANSFER' && hasUniquenessPending)}
          onClick={handleSave}
          sx={{ backgroundColor: '#204c77', '&:hover': { backgroundColor: '#1a3a5c' } }}
        >
          {mode === 'insert' ? 'Insert Step' : 'Add Step'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const __addStepModalInternal = {
  validateRangeParameters,
  validateMandatoryParameters,
  findFirstMandatoryValidationTarget,
  collectUniqueCandidates,
};
